import React, { useCallback, useMemo, useRef, useState } from 'react';
import Button from '@components/atoms/button/button';
import useHotfiltersToDisplay from '@hooks/filters/use-hotfilters-to-display';
import CatalogHotfilterItem from '@components/catalog/catalog-hotfilters/catalog-hotfilter-item/catalog-hotfilter-item';
import {
  HotFilterGroupConfiguration,
  hotfilterGroupConfigurations,
  HotFilterTypes,
} from '@config/catalog/hotfilter-group-configurations';
import CatalogBooleanHotfilter from '@components/catalog/catalog-hotfilters/catalog-boolean-hotfilter/catalog-boolean-hotfilter';
import CatalogHotfilterSortby from '@components/catalog/catalog-hotfilters/catalog-hotfilter-item/catalog-hotfilter-sortby';
import { CatalogCategoryHotfilterProvider } from '@context/catalog-category-hotfilter.context';
import { CatalogMaterialHotfilterProvider } from '@context/catalog-material-hotfilter.context';
import { useTranslation } from 'next-i18next';
import CatalogHotfiltersScrollWrapper from '@components/catalog/catalog-hotfilters/catalog-hotfilters-scroll-wrapper/catalog-hotfilters-scroll-wrapper';
import { getHotfilterButtonId } from '@components/organisms/hot-filters/hot-filter-button/hot-filter-button';
import styles from './catalog-hotfilters.module.scss';

const NR_OF_FILTERS_TO_DISPLAY = 8;

const CatalogHotfilters = ({ scrollRef }: { scrollRef: React.MutableRefObject<HTMLDivElement> }) => {
  const { filterGroupsToDisplay: allFilterGroups } = useHotfiltersToDisplay(hotfilterGroupConfigurations);
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const isLayoutChangeRef = useRef(false);
  const filterGroupsToDisplay = useMemo(
    () => (showAllFilters ? allFilterGroups : allFilterGroups.slice(0, NR_OF_FILTERS_TO_DISPLAY)),
    [allFilterGroups, showAllFilters],
  );

  // focus the next (previously hidden) filter button when expanding the hotfilters
  // for accessibility purposes
  const expandedNextFilterId = useMemo(() => {
    const nextLabel = allFilterGroups[NR_OF_FILTERS_TO_DISPLAY]?.label;
    return nextLabel ? getHotfilterButtonId(nextLabel) : undefined;
  }, [allFilterGroups]);

  const handleExpandCollapseClick = () => {
    isLayoutChangeRef.current = true;
    setShowAllFilters((prev) => !prev);
    // handling focus to the next eligible button when user hits expand button
    if (!showAllFilters && expandedNextFilterId) {
      window.requestAnimationFrame(() => {
        document.getElementById(expandedNextFilterId)?.focus();
      });
    }
    setTimeout(() => {
      // this ref is used to ensure that the hotfilter row doesn't disappear when the user hits expand/collapse button
      isLayoutChangeRef.current = false;
    }, 300);
  };

  const renderHotfilterItem = useCallback(
    (filterGroup: HotFilterGroupConfiguration) => {
      switch (filterGroup.type) {
        case HotFilterTypes.BOOLEAN:
          return <CatalogBooleanHotfilter filterGroup={filterGroup} />;
        case HotFilterTypes.CATEGORY:
          return (
            <CatalogCategoryHotfilterProvider>
              <CatalogHotfilterItem
                filterGroup={filterGroup}
                setIsFilterOpen={setIsFilterOpen}
              />
            </CatalogCategoryHotfilterProvider>
          );
        case HotFilterTypes.MATERIAL:
          return (
            <CatalogMaterialHotfilterProvider>
              <CatalogHotfilterItem
                filterGroup={filterGroup}
                setIsFilterOpen={setIsFilterOpen}
              />
            </CatalogMaterialHotfilterProvider>
          );
        default:
          return (
            <CatalogHotfilterItem
              filterGroup={filterGroup}
              setIsFilterOpen={setIsFilterOpen}
            />
          );
      }
    },
    [setIsFilterOpen],
  );

  return (
    <CatalogHotfiltersScrollWrapper
      scrollRef={scrollRef}
      isFilterOpen={isFilterOpen}
      isLayoutChangeRef={isLayoutChangeRef}
      hasFilters={allFilterGroups.length > 0}
    >
      {filterGroupsToDisplay.length > 0 && (
        <li>
          <CatalogHotfilterSortby setIsFilterOpen={setIsFilterOpen} />
        </li>
      )}
      {filterGroupsToDisplay.map((filterGroup) => (
        <li key={`catalog-hotfilter-item-${filterGroup.label}`}>{renderHotfilterItem(filterGroup)}</li>
      ))}
      {allFilterGroups.length > NR_OF_FILTERS_TO_DISPLAY && (
        <li>
          <Button
            disableDefaultStyling
            onClick={handleExpandCollapseClick}
            className={styles.catalogHotfilters__expandCollapseButton}
          >
            <span className={styles.catalogHotfilters__expandCollapseButton__text}>
              {showAllFilters ? t('CATALOG.FILTERS.LESS_HOTFILTERS') : t('CATALOG.FILTERS.ALL_HOTFILTERS')}
            </span>
            {showAllFilters ? '—' : `+`}
          </Button>
        </li>
      )}
    </CatalogHotfiltersScrollWrapper>
  );
};

export default CatalogHotfilters;
